import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Download, Printer } from 'lucide-react'
import { getJobsWip } from '../../api/accounting'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { HelpTip } from '../../components/HelpTip'
import { StatusBadge } from '../../components/PageParts'
import { fmtDay, localToday } from '../../utils/format'
import { money } from '../sales/salesShared'
import { ReportsTabs, StatementHead, downloadCsv, useCompanyName } from './reportsShared'
import { printPage, useAutoPrint } from '../../utils/print'

const amt = (v) => (Math.abs(Number(v || 0)) < 0.005 ? '–' : money(v))
const monthOf = (day) => new Date(`${day}T00:00:00`).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })

// Jobs in progress: for each long job, how far along it is, what it has
// earned against what's been billed, and the month-end adjustment.
export default function JobsInProgress() {
  const company = useCompanyName()
  const [params, setParams] = useSearchParams()
  const asOf = params.get('as_of') || localToday()
  const [data, setData] = useState(null)
  useAutoPrint(!!data)
  const [error, setError] = useState('')

  useEffect(() => {
    setData(null)
    setError('')
    getJobsWip(asOf).then(setData).catch((e) => setError(e.message || 'Could not load jobs in progress.'))
  }, [asOf])

  const exportCsv = () => downloadCsv('jobs_in_progress', [
    ['Job', 'Price', 'Expected cost', 'Cost so far', 'Done %', 'Earned', 'Billed', 'Earned, not yet billed', 'Billed, not yet earned', 'Expected loss'],
    ...data.rows.map((r) => [r.label, r.price, r.estimated_cost, r.costs, r.percent ?? '', r.earned ?? '', r.billed, r.under, r.over, r.loss]),
  ])

  return (
    <div className="ci-page fs-page">
      <ReportsTabs />
      <div className="ci-header-row fs-noprint">
        <div className="ci-title-block">
          <div>
            <h1>Jobs in progress <HelpTip text="For long jobs: income counts as the work is done, not when you invoice. This shows how far along each job is, what it has earned and what's been billed, and the difference the month-end adjustment books." /></h1>
            <div className="ci-title-meta">Long jobs: how far along, earned against billed.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          <button type="button" className="ci-btn ci-btn-ghost" disabled={!data?.rows?.length} onClick={exportCsv}><Download size={15} />Export</button>
          <button type="button" className="ci-btn ci-btn-ghost" disabled={!data} onClick={() => printPage()}><Printer size={15} />Print</button>
        </div>
      </div>

      <div className="ci-toolbar fs-noprint">
        <label className="fs-inline">As of <input className="ci-select" type="date" value={asOf} onChange={(e) => setParams({ as_of: e.target.value }, { replace: true })} /></label>
      </div>

      {error && <ErrorBanner message={error} />}

      {!data ? (!error && <Spinner label="Working out jobs in progress…" />) : !data.rows.length ? (
        <section className="fs-card">
          <p className="sum-muted">No long jobs yet. On a job’s page, tick <em>Long job: count income as the work is done</em> and enter its expected total cost.</p>
        </section>
      ) : (
        <section className="fs-card">
          <StatementHead company={company} title="Jobs in progress" sub={`As of ${fmtDay(data.as_of)}`} />
          <div className="fs-check fs-noprint">
            {data.run
              ? <StatusBadge label={`${monthOf(data.as_of)} adjustment posted`} tone="good" />
              : <Link to="/accounting/month-end">Post the adjustment at month-end close</Link>}
          </div>
          <div className="ci-table-scroll">
            <table className="fs-table">
              <thead>
                <tr>
                  <th>Job</th>
                  <th className="num hide-sm">Price <HelpTip text="What the customer pays for the whole job, before tax: as set on the job, or its quotes, plus approved changes." /></th>
                  <th className="num hide-sm">Expected cost <HelpTip text="What the whole job is expected to cost, entered on the job, plus the cost of approved changes." /></th>
                  <th className="num hide-sm">Cost so far</th>
                  <th className="num">Done <HelpTip text="Cost so far ÷ expected cost, at most 100%." /></th>
                  <th className="num">Earned <HelpTip text="Done × price: the income the work so far has earned." /></th>
                  <th className="num">Billed <HelpTip text="Income already counted from the job’s invoices (not deposits, which are held until the work is invoiced)." /></th>
                  <th className="num">Earned, not yet billed <HelpTip text="More work done than invoiced. Booked as a contract asset (less any deposit held for the job)." /></th>
                  <th className="num">Billed, not yet earned <HelpTip text="Invoiced ahead of the work. Booked as a contract liability, and income comes down to what’s earned." /></th>
                  <th className="num hide-sm">Expected loss <HelpTip text="When a job will cost more than its price, the rest of the expected loss is booked now, not as the work goes on." /></th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.job_id}>
                    <td>
                      <Link to={`/jobs/${r.job_id}`}>{r.name}</Link>
                      <span className="cell-sub">{r.job_number}{r.problem ? <span className="sum-warn"> · {r.problem}</span> : ''}</span>
                    </td>
                    <td className="num hide-sm">{amt(r.price)}</td>
                    <td className="num hide-sm">{amt(r.estimated_cost)}</td>
                    <td className="num hide-sm">{amt(r.costs)}</td>
                    <td className="num">{r.percent == null ? '–' : `${r.percent}%`}</td>
                    <td className="num">{r.earned == null ? '–' : amt(r.earned)}</td>
                    <td className="num">{amt(r.billed)}</td>
                    <td className="num">{amt(r.under)}</td>
                    <td className="num">{amt(r.over)}</td>
                    <td className="num hide-sm">{amt(r.loss)}</td>
                  </tr>
                ))}
                <tr className="fs-grand">
                  <td>Total</td>
                  <td className="num hide-sm">{amt(data.totals.price)}</td>
                  <td className="num hide-sm">{amt(data.totals.estimated_cost)}</td>
                  <td className="num hide-sm">{amt(data.totals.costs)}</td>
                  <td />
                  <td className="num">{amt(data.totals.earned)}</td>
                  <td className="num">{amt(data.totals.billed)}</td>
                  <td className="num">{amt(data.totals.under)}</td>
                  <td className="num">{amt(data.totals.over)}</td>
                  <td className="num hide-sm">{amt(data.totals.loss)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  )
}
